import {CommonModule} from '@angular/common';
import {Component, OnInit, inject} from '@angular/core';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {ActivatedRoute} from '@angular/router';
import {catchError, finalize, of} from 'rxjs';
import {
  AccessMonitoringSetupService,
  SlackInfo
} from '../../services/access-monitoring-setup.service';

import {TranslateModule} from '@ngx-translate/core';

@Component({
  selector: 'app-slack-connect-redirect',
  standalone: true,
  imports: [CommonModule, MatProgressSpinnerModule, TranslateModule],
  templateUrl: './slack-connect-redirect.component.html',
  styleUrls: ['./slack-connect-redirect.component.scss']
})
export class SlackConnectRedirectComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly accessMonitoringSetupService = inject(AccessMonitoringSetupService);

  public loading = true;
  public errorMessage: string | null = null;

  public ngOnInit(): void {
    this.processConnection();
  }

  private processConnection(): void {
    const {teamId, effectiveReturnUrl} = this.getRedirectContext();

    if (!teamId) {
      this.loading = false;
      this.errorMessage = 'CHANGE_MONITORING.SLACK_SETUP_NO_ID';
      return;
    }

    if (!effectiveReturnUrl) {
      this.loading = false;
      this.errorMessage = 'CHANGE_MONITORING.SLACK_SETUP_NO_REDIRECT';
      return;
    }

    this.verifySlackConnection(teamId, effectiveReturnUrl);
  }

  private getRedirectContext(): {teamId: string | null; effectiveReturnUrl: string | null} {
    const teamId = this.accessMonitoringSetupService.getPendingSlackTeamId();
    const returnUrl = this.accessMonitoringSetupService.getPendingSlackReturnUrl();
    const slackError = this.route.snapshot.queryParamMap.get('slack_error');
    const effectiveReturnUrl =
      returnUrl && slackError
        ? this.appendQueryParam(returnUrl, 'slack_error', slackError)
        : returnUrl;

    return {teamId, effectiveReturnUrl};
  }

  private verifySlackConnection(teamId: string, returnUrl?: string): void {
    this.accessMonitoringSetupService
      .getSlackConnection(teamId)
      .pipe(
        catchError(() => {
          this.errorMessage = 'CHANGE_MONITORING.SLACK_SETUP_CANT_CONNECT';
          return of(null);
        }),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe((slackInfo: SlackInfo | null) => {
        if (!slackInfo) {
          return;
        }

        this.onSlackConnectionVerified(returnUrl);
      });
  }

  private onSlackConnectionVerified(returnUrl?: string): void {
    this.openReturnUrl(returnUrl);
  }

  private appendQueryParam(url: string, key: string, value: string): string {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.set(key, value);
    const asString = parsed.toString();
    return url.startsWith('http') ? asString : asString.replace(window.location.origin, '');
  }

  private openReturnUrl(returnUrl?: string): void {
    if (returnUrl) {
      window.location.assign(returnUrl);
    }
  }
}
